import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Inbox, Search, Mail, Copy, Check, Trash2, UserPlus, UserCheck, Loader2 } from "lucide-react";

const STATUS = {
  nuova: { label: "Nuova", cls: "bg-amber-100 text-amber-700" },
  contattato: { label: "Contattato", cls: "bg-blue-100 text-blue-700" },
  cliente: { label: "Cliente", cls: "bg-emerald-100 text-emerald-700" },
};
const statusOf = (l) => (l.status && STATUS[l.status] ? l.status : "nuova");
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } };

export default function Contatti() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManager = user && (user.role === "chef" || user.role === "comandante");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [copied, setCopied] = useState("");
  const [approving, setApproving] = useState("");
  const [granted, setGranted] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/leads").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { if (isManager) load(); else setLoading(false); }, [isManager]);

  const counts = useMemo(() => {
    const c = { all: items.length, nuova: 0, contattato: 0, cliente: 0 };
    items.forEach((l) => { c[statusOf(l)] += 1; });
    return c;
  }, [items]);

  const list = useMemo(() => {
    let l = items;
    if (filter !== "all") l = l.filter((x) => statusOf(x) === filter);
    const s = q.trim().toLowerCase();
    if (s) l = l.filter((x) => (x.name || "").toLowerCase().includes(s) || (x.email || "").toLowerCase().includes(s));
    return l;
  }, [items, filter, q]);

  const setStatus = async (id, status) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
    try { await api.patch(`/leads/${id}/status`, { status }); }
    catch { toast.error("Aggiornamento non riuscito"); load(); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Eliminare il contatto ${l.name}?`)) return;
    setItems((prev) => prev.filter((x) => x.id !== l.id));
    try { await api.delete(`/leads/${l.id}`); toast.success("Contatto eliminato"); }
    catch { toast.error("Eliminazione non riuscita"); load(); }
  };

  const copyEmail = async (email) => {
    try { await navigator.clipboard.writeText(email); setCopied(email); toast.success("Email copiata"); setTimeout(() => setCopied(""), 1600); }
    catch { toast.error("Copia non riuscita"); }
  };

  const grantAccess = (l) => {
    if (statusOf(l) === "nuova") setStatus(l.id, "contattato");
    navigate("/utenti", { state: { prefillName: l.name, prefillEmail: l.email } });
  };

  const approve = async (l) => {
    if (!window.confirm(`Accettare ${l.name || l.email} e creare l'accesso?\n\nVerrà creato un utente (Armatore, sola lettura) con una password sicura e inviata l'email di benvenuto con le credenziali.`)) return;
    setApproving(l.id);
    try {
      const r = await api.post(`/leads/${l.id}/approve`);
      setItems((prev) => prev.map((x) => (x.id === l.id ? { ...x, status: "cliente" } : x)));
      setGranted((g) => ({ ...g, [l.id]: { email: r.data.email, password: r.data.password, email_sent: r.data.email_sent } }));
      if (r.data.email_sent) toast.success(`Accesso creato ed email inviata a ${r.data.email}`);
      else toast.warning("Accesso creato. L'email non è partita: copia e invia tu le credenziali qui sotto.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operazione non riuscita");
    } finally {
      setApproving("");
    }
  };

  if (!isManager) {
    return <div data-testid="contatti-denied" className="rounded-2xl bg-white p-10 text-center text-slate-500">Sezione riservata a Chef e Comandante.</div>;
  }

  return (
    <div className="space-y-6" data-testid="contatti-page">
      <div>
        <h1 className="font-head text-3xl font-bold text-navy">Contatti & Richieste di accesso</h1>
        <p className="mt-1 text-sm text-slate-500">Chi ha lasciato nome ed email (pagina promo, QR dello stand). Gestisci i follow-up e dai l'accesso in un clic.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[["all", "Tutti"], ["nuova", "Nuove"], ["contattato", "Contattati"], ["cliente", "Clienti"]].map(([k, l]) => (
          <button
            key={k}
            data-testid={`contatti-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${filter === k ? "bg-navy text-white" : "bg-white text-navy hover:bg-slate-100"}`}
          >
            {l} <span className="opacity-60">{counts[k]}</span>
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="contatti-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome o email…" className="h-11 w-full rounded-lg border-2 border-slate-200 pl-10 pr-3 outline-none focus:border-gold" />
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl bg-white p-10 text-center text-slate-400">Caricamento…</div>
      ) : list.length === 0 ? (
        <div data-testid="contatti-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <Inbox className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-slate-500">{items.length === 0 ? "Ancora nessun contatto. Appariranno qui quando qualcuno lascia nome ed email." : "Nessun contatto per questo filtro."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((l) => {
            const st = statusOf(l);
            return (
              <div key={l.id} data-testid={`contatto-${l.id}`} className="rounded-2xl border border-navy/10 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-navy font-head text-sm font-bold text-gold">
                      {(l.name || "?").trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-head font-semibold text-navy">{l.name || "—"}</div>
                      <button onClick={() => copyEmail(l.email)} className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy" data-testid={`contatto-copy-${l.id}`}>
                        <Mail className="h-3.5 w-3.5" /> {l.email}
                        {copied === l.email ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 opacity-50" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS[st].cls}`}>{STATUS[st].label}</span>
                    <span className="text-xs text-slate-400">{fmtDate(l.created_at)}</span>
                    <button data-testid={`contatto-delete-${l.id}`} onClick={() => remove(l)} className="rounded-lg p-2 text-slate-300 transition-colors hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
                  <div className="flex flex-wrap gap-1.5">
                    {Object.keys(STATUS).map((s) => (
                      <button key={s} data-testid={`contatto-status-${l.id}-${s}`} onClick={() => setStatus(l.id, s)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${st === s ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                        {STATUS[s].label}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <a href={`mailto:${l.email}?subject=${encodeURIComponent("Yacht Food Cost — la tua prova gratuita")}`} className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-navy hover:bg-slate-200"><Mail className="h-4 w-4" /> Scrivi</a>
                    <button data-testid={`contatto-grant-${l.id}`} onClick={() => grantAccess(l)} className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-navy hover:bg-slate-200"><UserPlus className="h-4 w-4" /> Personalizza</button>
                    <button data-testid={`contatto-accept-${l.id}`} onClick={() => approve(l)} disabled={approving === l.id || st === "cliente"} className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
                      {approving === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />} Accetta con un click
                    </button>
                  </div>
                </div>
                {granted[l.id] && (
                  <div data-testid={`contatto-cred-${l.id}`} className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm">
                    <div className="font-semibold text-emerald-800">{granted[l.id].email_sent ? "Accesso creato — email di benvenuto inviata ✓" : "Accesso creato — l'email non è partita, invia tu le credenziali"}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-navy">
                      <span><b>Email:</b> {granted[l.id].email}</span>
                      <span><b>Password:</b> <code className="rounded bg-white px-1.5 py-0.5">{granted[l.id].password}</code></span>
                      <button data-testid={`contatto-copy-cred-${l.id}`} onClick={() => copyEmail(`Email: ${granted[l.id].email}\nPassword: ${granted[l.id].password}`)} className="flex items-center gap-1 rounded-md bg-navy px-2 py-1 text-xs font-semibold text-white hover:bg-navy-hover"><Copy className="h-3 w-3" /> Copia credenziali</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
